import { supabase } from '../utils/supabase/client';

/**
 * 🎯 Service de données centralisé pour Supabase
 * Gère toutes les interactions avec la base de données
 */

// ============================================
// 👤 GESTION DES UTILISATEURS
// ============================================

export const userDataService = {
  // Récupérer le profil utilisateur complet
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          investments:investments(
            id,
            amount,
            current_value,
            status,
            invested_at,
            project:projects(
              id,
              title,
              sector,
              status
            )
          )
        `)
        .eq('id', userId)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur récupération profil utilisateur:', error);
      return { success: false, error: error.message };
    }
  },

  // Mettre à jour le profil utilisateur
  async updateUserProfile(userId, profileData) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur mise à jour profil:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les analytics utilisateur
  async getUserAnalytics(userId) {
    try {
      const { data, error } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('user_id', userId)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { success: true, data: data || null };
    } catch (error) {
      console.error('❌ Erreur récupération analytics:', error);
      return { success: false, error: error.message };
    }
  },

  // Calculer et sauvegarder les analytics
  async calculateAndSaveAnalytics(userId) {
    try {
      // Récupérer les investissements actifs
      const { data: investments, error: investError } = await supabase
        .from('investments')
        .select(`
          amount,
          current_value,
          realized_return,
          unrealized_return,
          project:projects(impact_score)
        `)
        .eq('investor_id', userId)
        .eq('status', 'active');

      if (investError) throw investError;

      // Calculer les métriques
      const totalInvested = investments.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);
      const portfolioValue = investments.reduce((sum, inv) => sum + parseFloat(inv.current_value || inv.amount || 0), 0);
      const totalReturns = investments.reduce((sum, inv) => 
        sum + parseFloat(inv.realized_return || 0) + parseFloat(inv.unrealized_return || 0), 0);
      const impactScore = Math.round(investments.reduce((sum, inv) => 
        sum + (inv.project?.impact_score || 0), 0) / Math.max(investments.length, 1));

      // Sauvegarder les analytics
      const { data, error } = await supabase
        .from('user_analytics')
        .upsert({
          user_id: userId,
          portfolio_value: portfolioValue,
          total_invested: totalInvested,
          total_returns: totalReturns,
          impact_score: impactScore,
          calculated_at: new Date().toISOString().split('T')[0]
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur calcul analytics:', error);
      return { success: false, error: error.message };
    }
  }
};

// ============================================
// 💼 GESTION DES PROJETS
// ============================================

export const projectDataService = {
  // Récupérer tous les projets avec statistiques
  async getAllProjects(filters = {}) {
    try {
      let query = supabase
        .from('projects')
        .select(`
          *,
          investments:investments(
            id,
            amount,
            investor_id
          )
        `)
        .eq('status', 'active');

      // Appliquer les filtres
      if (filters.sector) {
        query = query.eq('sector', filters.sector);
      }
      if (filters.minAmount) {
        query = query.gte('minimum_investment', filters.minAmount);
      }
      if (filters.maxRisk) {
        query = query.lte('risk_level', filters.maxRisk);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Enrichir avec les statistiques calculées
      const enrichedData = data.map(project => ({
        ...project,
        raised_amount: project.investments?.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0) || 0,
        investors_count: new Set(project.investments?.map(inv => inv.investor_id) || []).size,
        funding_progress: project.target_amount > 0 ? 
          Math.round((project.investments?.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0) || 0) / project.target_amount * 100) : 0
      }));

      return { success: true, data: enrichedData };
    } catch (error) {
      console.error('❌ Erreur récupération projets:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer un projet spécifique avec détails
  async getProjectById(projectId) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          investments:investments(
            id,
            amount,
            investor_id,
            invested_at,
            status
          )
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;

      // Enrichir avec les statistiques
      const enrichedData = {
        ...data,
        raised_amount: data.investments?.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0) || 0,
        investors_count: new Set(data.investments?.map(inv => inv.investor_id) || []).size,
        funding_progress: data.target_amount > 0 ? 
          Math.round((data.investments?.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0) || 0) / data.target_amount * 100) : 0,
        recent_investments: data.investments?.slice(-5) || []
      };

      return { success: true, data: enrichedData };
    } catch (error) {
      console.error('❌ Erreur récupération projet:', error);
      return { success: false, error: error.message };
    }
  },

  // Créer un nouveau projet
  async createProject(projectData) {
    try {
      // Injecter automatiquement le creator_id si absent
      if (!projectData.creator_id) {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        const userId = userData?.user?.id;
        if (!userId) {
          throw new Error('Utilisateur non authentifié: impossible de définir creator_id');
        }
        projectData = { ...projectData, creator_id: userId };
      }

      const { data, error } = await supabase
        .from('projects')
        .insert({
          ...projectData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur création projet:', error);
      return { success: false, error: error.message };
    }
  },

  // Mettre à jour un projet
  async updateProject(projectId, projectData) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update({
          ...projectData,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur mise à jour projet:', error);
      return { success: false, error: error.message };
    }
  }
};

// ============================================
// 💰 GESTION DES INVESTISSEMENTS
// ============================================

export const investmentDataService = {
  // Récupérer les investissements d'un utilisateur
  async getUserInvestments(userId) {
    try {
      const { data, error } = await supabase
        .from('investments')
        .select(`
          *,
          project:projects(
            id,
            title,
            sector,
            status,
            expected_return,
            risk_level
          )
        `)
        .eq('investor_id', userId)
        .order('invested_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur récupération investissements:', error);
      return { success: false, error: error.message };
    }
  },

  // Créer un nouvel investissement
  async createInvestment(investmentData) {
    try {
      const { data, error } = await supabase
        .from('investments')
        .insert({
          ...investmentData,
          invested_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur création investissement:', error);
      return { success: false, error: error.message };
    }
  },

  // Mettre à jour un investissement
  async updateInvestment(investmentId, investmentData) {
    try {
      const { data, error } = await supabase
        .from('investments')
        .update({
          ...investmentData,
          updated_at: new Date().toISOString()
        })
        .eq('id', investmentId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur mise à jour investissement:', error);
      return { success: false, error: error.message };
    }
  }
};

// ============================================
// 🔔 GESTION DES NOTIFICATIONS
// ============================================

export const notificationDataService = {
  // Récupérer les notifications d'un utilisateur
  async getUserNotifications(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur récupération notifications:', error);
      return { success: false, error: error.message };
    }
  },

  // Marquer une notification comme lue
  async markNotificationAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur marquage notification:', error);
      return { success: false, error: error.message };
    }
  },

  // Créer une nouvelle notification
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          ...notificationData,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ Erreur création notification:', error);
      return { success: false, error: error.message };
    }
  }
};

// ============================================
// 📊 SERVICE GLOBAL
// ============================================

export const dataService = {
  user: userDataService,
  project: projectDataService,
  investment: investmentDataService,
  notification: notificationDataService,

  // Fonction utilitaire pour tester la connexion
  async testConnection() {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .select('count')
        .limit(1);

      if (error) throw error;
      return { success: true, message: 'Connexion Supabase réussie' };
    } catch (error) {
      console.error('❌ Erreur connexion Supabase:', error);
      return { success: false, error: error.message };
    }
  }
};

export default dataService;